import { Journey } from '@audibene/ta.utils-opportunity'
import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { AppointmentType } from '../../constants'
import { logFactory } from '../../util'
import { transformAsegoToAvailabilities } from '../../util/transformAsegoToAvailabilities'
import { useAppointmentsService } from '../services'
import { GetAvailabilitiesRequest } from '../services/appointments/availabilities'
import { Resource } from 'types'

const hookName = 'useAsegoAvailabilityDays'
const log = logFactory(hookName)

interface Props {
  appointmentType: AppointmentType
  dateEnd: Date
  dateStart: Date
  durationInMinutes?: number
  isEnabled?: boolean
  journey: Journey
  resources: Resource[]
  timeZone: string
}

const useAsegoAvailabilityDays = ({
  appointmentType,
  dateEnd,
  dateStart,
  durationInMinutes,
  isEnabled = true,
  journey,
  resources,
  timeZone,
}: Props) => {
  const { getAsegoDays, isReady } = useAppointmentsService()

  const params: GetAvailabilitiesRequest = {
    allow_integration_errors: true,
    appointment_type: appointmentType,
    customer_timezone: timeZone,
    date_end: dateEnd,
    date_start: dateStart,
    duration_in_minutes: durationInMinutes,
    journey,
    resource_gids: resources.map((resource) => resource.gid),
  }

  const fetchAsegoDays = async () => {
    log(`📅 Fetching Asego availability days...`, params)

    const timeStart = Date.now()
    const days = await getAsegoDays(params)
    const timeEnd = Date.now()
    const timeInMs = timeEnd - timeStart

    if (days.responseStatus === 200) {
      log(`📅 Found Asego availability days (${timeInMs}ms)`, days)
    } else {
      log(`⚠️ Failed to fetch Asego availability days (${timeInMs}ms)`, {
        status: days.responseStatus,
      })
    }

    return days
  }

  const results = useQuery([hookName, params], fetchAsegoDays, {
    enabled: isReady && isEnabled,
  })

  const { isLoading, data: asegoDays } = results

  // Transform asego days to availabilities format
  const transformedData = useMemo(() => transformAsegoToAvailabilities(asegoDays), [asegoDays])

  return {
    ...results,
    data: transformedData,
    isLoading,
  }
}

export { useAsegoAvailabilityDays }
